'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface CareersSectionProps {
  className?: string;
}

interface CareerOpportunity {
  id: number;
  title: string;
  department: string;
  location: string;
  type: string;
  description: string;
}

const CareersSection = ({ className = '' }: CareersSectionProps) => {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const opportunities: CareerOpportunity[] = [
    {
      id: 1,
      title: 'Senior AI Transformation Consultant',
      department: 'Consulting',
      location: 'London, UK (Hybrid)',
      type: 'Full-time',
      description: 'Lead enterprise AI transformation engagements, working directly with C-suite executives to design and implement change strategies. Requires 8+ years consulting experience with proven track record in AI/digital transformation.',
    },
    {
      id: 2,
      title: 'Change Management Specialist',
      department: 'Delivery',
      location: 'Remote (UK-based)',
      type: 'Full-time',
      description: 'Design and deliver change management programs for AI adoption initiatives. Requires 5+ years experience in organizational change, strong facilitation skills, and understanding of AI transformation challenges.',
    },
    {
      id: 3,
      title: 'Learning & Development Lead',
      department: 'Capability Building',
      location: 'London, UK',
      type: 'Full-time',
      description: 'Create learning frameworks and capability building programs for AI transformation. Requires expertise in instructional design, adult learning principles, and experience with technology adoption programs.',
    },
    {
      id: 4,
      title: 'Partner Network Manager',
      department: 'Business Development',
      location: 'Remote (UK-based)',
      type: 'Full-time',
      description: 'Build and manage our curated partner network, identifying and onboarding senior consultants. Requires strong relationship management skills and understanding of consulting industry dynamics.',
    },
  ];

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <section className={`py-16 lg:py-24 bg-gradient-to-br from-primary/5 to-secondary/5 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-headline text-primary mb-6">
            Join Our Team
          </h2>
          <p className="text-lg text-muted-foreground font-body leading-relaxed mb-8">
            We\'re building a world-class team of AI transformation experts. If you\'re passionate about helping organizations succeed with AI through people-first leadership, we\'d love to hear from you.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center space-x-2 bg-card px-4 py-2 rounded-lg border border-border">
              <Icon name="UserGroupIcon" size={20} className="text-secondary" variant="solid" />
              <span className="text-sm font-semibold font-body text-foreground">Collaborative Culture</span>
            </div>
            <div className="flex items-center space-x-2 bg-card px-4 py-2 rounded-lg border border-border">
              <Icon name="AcademicCapIcon" size={20} className="text-secondary" variant="solid" />
              <span className="text-sm font-semibold font-body text-foreground">Continuous Learning</span>
            </div>
            <div className="flex items-center space-x-2 bg-card px-4 py-2 rounded-lg border border-border">
              <Icon name="ChartBarIcon" size={20} className="text-secondary" variant="solid" />
              <span className="text-sm font-semibold font-body text-foreground">Impactful Work</span>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto space-y-4">
          {opportunities.map((opportunity) => (
            <div
              key={opportunity.id}
              className="bg-card rounded-lg border border-border shadow-card hover:shadow-strong transition-all duration-300 overflow-hidden"
            >
              <button
                onClick={() => toggleExpand(opportunity.id)}
                className="w-full p-6 text-left flex items-center justify-between hover:bg-muted/30 transition-colors duration-300"
              >
                <div className="flex-1">
                  <h3 className="text-xl font-bold font-headline text-foreground mb-2">
                    {opportunity.title}
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    <span className="flex items-center space-x-1 text-sm text-muted-foreground font-body">
                      <Icon name="BriefcaseIcon" size={16} variant="outline" />
                      <span>{opportunity.department}</span>
                    </span>
                    <span className="flex items-center space-x-1 text-sm text-muted-foreground font-body">
                      <Icon name="MapPinIcon" size={16} variant="outline" />
                      <span>{opportunity.location}</span>
                    </span>
                    <span className="flex items-center space-x-1 text-sm text-muted-foreground font-body">
                      <Icon name="ClockIcon" size={16} variant="outline" />
                      <span>{opportunity.type}</span>
                    </span>
                  </div>
                </div>
                <div className="ml-4">
                  <Icon
                    name={expandedId === opportunity.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                    size={24}
                    className="text-secondary"
                    variant="outline"
                  />
                </div>
              </button>

              {expandedId === opportunity.id && (
                <div className="px-6 pb-6 border-t border-border">
                  <p className="text-muted-foreground font-body leading-relaxed mb-4 mt-4">
                    {opportunity.description}
                  </p>
                  <Link
                    href="/contact"
                    className="inline-flex items-center space-x-2 px-6 py-2.5 rounded-md font-semibold font-cta text-sm text-secondary-foreground bg-secondary hover:bg-secondary/90 transition-all duration-300 shadow-subtle hover:shadow-strong"
                  >
                    <span>Apply Now</span>
                    <Icon name="ArrowRightIcon" size={16} variant="outline" />
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="max-w-3xl mx-auto text-center mt-12">
          <p className="text-muted-foreground font-body mb-6">
            Don\'t see the right role? We\'re always interested in connecting with exceptional talent.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center space-x-2 text-secondary hover:text-secondary/80 font-semibold font-body transition-colors duration-300"
          >
            <span>Send us your CV</span>
            <Icon name="ArrowRightIcon" size={20} variant="outline" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CareersSection;